import React from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { Theme } from '@mui/material';

/** Class names for the controls inside a FilterBar (search, select, date, clear). */
export const useFilterBarStyles = createUseStyles((theme: Theme) => {
    const { color, mq } = theme.custom;
    return {
        root: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            backgroundColor: color.surfaceMuted,
            borderBottom: `1px solid ${color.border}`,
            [mq.sm]: {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                padding: 12,
            },
        },
        search: {
            width: 220,
            [mq.md]: { width: 190 },
            [mq.sm]: { width: 'auto', gridColumn: '1 / -1' },
        },
        text: { width: 180, [mq.sm]: { width: 'auto' } },
        select: { width: 160, [mq.sm]: { width: 'auto' } },
        date: { width: 160, [mq.sm]: { width: 'auto' } },
        clear: { marginLeft: 'auto', [mq.sm]: { marginLeft: 0, gridColumn: '1 / -1', justifySelf: 'end' } },
        hint: { width: '100%', fontSize: 12, color: color.tone.danger.onSoft, margin: 0 },
        sectionLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.text.secondary, margin: '4px 0 6px' },
    };
});

export interface FilterBarProps {
    /** Accessible name of the search region, e.g. "Order filters". */
    label: string;
    className?: string;
    children: React.ReactNode;
}

/** The muted strip at the top of a list panel that holds its filters; see DESIGN.md §5 Filters. */
const FilterBar = ({ label, className, children }: FilterBarProps) => {
    const classes = useFilterBarStyles();
    return (
        <div className={classNames(classes.root, className)} role="search" aria-label={label}>
            {children}
        </div>
    );
};

export default FilterBar;
